/**
 * @file PlaceholderTextarea.tsx
 * @description Поле текста согласия со вставками (phase19.16, Р3г;
 * uiux-r3g-consent-texts §3, §5): подпись, поле, кнопки «+ {categories}» /
 * «+ {services}» (вставка в место курсора, 48 px), подсказка, счётчик
 * знаков (предел — из shared), ошибка (`aria-invalid` + `aria-describedby`),
 * мягкое предупреждение и подсветка «изменено от ред. N».
 */

import { CONSENT_TEXT_PLACEHOLDERS } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useRef, type ReactNode } from "react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { cn } from "@/lib/utils";

interface Props {
  /** id поля в DOM (ссылки сводки ошибок). */
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  max: number;
  hint?: string;
  error?: string;
  warning?: string;
  /** Показать кнопки вставок. */
  placeholders?: boolean;
  /** Во что превратятся вставки у образца. */
  placeholderHint?: ReactNode;
  multiline?: boolean;
  /** Поле отличается от основы и подсветка включена: подпись «Изменено от ред. N». */
  changedNote?: string;
  readOnly?: boolean;
}

export function PlaceholderTextarea(props: Props) {
  const { id, label, value, onChange, max, hint, error, warning, placeholders, placeholderHint, multiline, changedNote, readOnly } = props;
  const ref = useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const length = [...value].length;
  const describedBy = [`${id}-meta`, error ? `${id}-error` : null, warning && !error ? `${id}-warning` : null]
    .filter(Boolean)
    .join(" ");

  function insert(name: string) {
    const el = ref.current;
    const token = `{${name}}`;
    const startPos = el?.selectionStart ?? value.length;
    const endPos = el?.selectionEnd ?? value.length;
    onChange(value.slice(0, startPos) + token + value.slice(endPos));
    // Курсор — сразу за вставкой, фокус возвращается в поле.
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(startPos + token.length, startPos + token.length);
    });
  }

  const common = {
    id,
    ref,
    value,
    readOnly,
    "aria-invalid": !!error,
    "aria-describedby": describedBy,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
  };

  return (
    <div
      data-changed={changedNote ? "" : undefined}
      className={cn(
        "flex min-w-0 scroll-mt-24 flex-col gap-1 rounded-lg",
        changedNote && "bg-primary/5 px-3 py-2 [box-shadow:inset_3px_0_0_var(--primary)]",
      )}
    >
      <Label htmlFor={id}>{label}</Label>
      {multiline ? <Textarea {...common} rows={2} className="min-h-12" /> : <Input {...common} className="min-h-12" />}
      {placeholders && !readOnly ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-caption text-on-surface-variant">Вставить:</span>
          {CONSENT_TEXT_PLACEHOLDERS.map((name) => (
            <Button key={name} type="button" variant="outline" className="min-h-12" onClick={() => insert(name)}>
              <Plus className="size-4" aria-hidden />
              {`{${name}}`}
            </Button>
          ))}
        </div>
      ) : null}
      <div id={`${id}-meta`} className="flex flex-wrap justify-between gap-x-3">
        <span className="type-caption min-w-0 break-words text-on-surface-variant">
          {changedNote ? <span className="text-primary">{changedNote}. </span> : null}
          {hint}
          {placeholderHint ? <span className="block">{placeholderHint}</span> : null}
        </span>
        <span className={cn("type-caption shrink-0 tabular-nums", length > max ? "text-destructive" : "text-on-surface-variant")}>
          {length}/{max}
        </span>
      </div>
      {error ? (
        <p id={`${id}-error`} className="type-caption text-destructive">
          {error}
        </p>
      ) : null}
      {warning && !error ? (
        <p id={`${id}-warning`} className="type-caption text-warning">
          {warning}
        </p>
      ) : null}
    </div>
  );
}
